import { ArrowRightIcon, Users2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Button, TextButton, usePageTitle } from 'slab'
import { useDeleteEmployersOwnersId, useGetEmployersEmployersIdOwners, usePostEmployersOwners, usePutEmployersOwnersId } from 'utils/web_api'
import { type EmployersComplianceOwner } from 'utils/web_api.schemas'

import { OwnerEntry } from './OwnerEntry'
import { TrustFooter } from './TrustFooter'
import { CenteredLayout } from '../employer_onboarding/CenteredLayout'
import { useEmployer } from '../employer_onboarding/EmployerProvider'

export function BeneficialOwnersPage () {
  usePageTitle('Add owners')
  const navigate = useNavigate()
  const { employer } = useEmployer()

  const [defaultCardsToOpen, setDefaultCardsToOpen] = useState(true)

  const methods = useForm<{
    owners: EmployersComplianceOwner[]
  }>({
    defaultValues: {
      owners: []
    }
  })

  const { setValue, watch } = methods

  const { fields: ownerFields, append: appendOwner, remove: removeOwner } = useFieldArray({
    control: methods.control,
    name: 'owners'
  })

  // Watch all owners to calculate total ownership
  const watchedOwners = watch('owners')

  const createOwnerMutation = usePostEmployersOwners()
  const updateOwnerMutation = usePutEmployersOwnersId()
  const deleteOwnerMutation = useDeleteEmployersOwnersId()

  // Fetch existing beneficial owners
  const { data: existingOwners, isLoading: isLoadingOwners, error: ownersError } = useGetEmployersEmployersIdOwners(
    employer?.id || '',
    {
      query: {
        enabled: !!employer?.id,
        select: (data) => {
          const filtered = data?.filter((owner: EmployersComplianceOwner) => {
            return owner.is_beneficial_owner
          }) || []
          return filtered
        }
      }
    }
  )

  // Populate form with existing beneficial owners when data is loaded
  useEffect(() => {
    if (existingOwners) {
      if (existingOwners.length > 0) {
        setValue('owners', existingOwners)
        setDefaultCardsToOpen(false)
      } else {
        // If no existing owners, clear the form and add an empty owner entry with accordion open
        setValue('owners', [])
        appendOwner({
          first_name: '',
          last_name: '',
          title: '',
          email: '',
          is_beneficial_owner: true,
          is_controlling_officer: false,
          id: ''
        })
        setDefaultCardsToOpen(true)
      }
    }
  }, [existingOwners, setValue, appendOwner])

  const handleBack = () => {
    // Check if any owner is marked as beneficial owner
    const hasBeneficialOwner = watchedOwners.some(owner => owner.is_beneficial_owner)

    if (hasBeneficialOwner) {
      navigate('/employers/verification/owner_invitation/add-individual')
    } else {
      navigate('/employers/verification/owner_invitation/ownership-information')
    }
  }

  const handleSubmit = () => {
    navigate('/employers/verification/owner_invitation/confirm-details')
  }

  const handleOwnerCreate = async (ownerData: EmployersComplianceOwner, ownerIndex: number): Promise<void> => {
    if (!employer?.id) {
      toast.error('Employer ID is required')
      throw new Error('Employer ID is required')
    }

    return new Promise((resolve, reject) => {
      createOwnerMutation.mutate({
        data: {
          first_name: ownerData.first_name,
          last_name: ownerData.last_name,
          email: ownerData.email,
          is_controlling_officer: false,
          title: ownerData.title,
          is_beneficial_owner: ownerData.is_beneficial_owner,
        }
      }, {
        onSuccess: (response) => {
          toast.success('Successfully added owner!')

          if (response?.id) {
            // Update the form data directly with the new owner ID
            const currentOwners = methods.getValues('owners')
            const updatedOwners = [...currentOwners]
            updatedOwners[ownerIndex] = {
              ...updatedOwners[ownerIndex],
              id: response.id
            }
            setValue('owners', updatedOwners)
          }

          resolve()
        },
        onError: (error: any) => {
          toast.error('Error adding owner. If this issue persists, please reach out to us at support@thatch.com')
          console.error('Failed to create owner:', error)
          reject(error)
        }
      })
    })
  }

  const handleOwnerUpdate = async (ownerId: string, ownerData: EmployersComplianceOwner): Promise<void> => {
    return new Promise((resolve, reject) => {
      updateOwnerMutation.mutate({
        id: ownerId,
        data: {
          first_name: ownerData.first_name,
          last_name: ownerData.last_name,
          email: ownerData.email,
          is_controlling_officer: ownerData.is_controlling_officer,
          is_beneficial_owner: true,
          title: ownerData.title,
        }
      }, {
        onSuccess: () => {
          toast.success('Successfully updated owner!')
          resolve()
        },
        onError: (error: any) => {
          toast.error('Error updating owner. If this issue persists, please reach out to us at support@thatch.com')
          console.error('Failed to update owner:', error)
          reject(error)
        }
      })
    })
  }

  const handleOwnerDelete = async (ownerId: string, ownerIndex: number): Promise<void> => {
    if (ownerId && ownerId !== '') {
      // Delete saved owner via API
      return new Promise((resolve, reject) => {
        if (ownerId && ownerId !== '') {
          deleteOwnerMutation.mutate({
            id: ownerId
          }, {
            onSuccess: () => {
              toast.success('Successfully deleted owner!')
              removeOwner(ownerIndex)
              resolve()
            },
            onError: (error: any) => {
              toast.error('Error deleting owner. If this issue persists, please reach out to us at support@thatch.com')
              console.error('Failed to delete owner:', error)
              reject(error)
            }
          })
        }
      })
    } else {
      // Remove unsaved owner from form array
      removeOwner(ownerIndex)
      return Promise.resolve()
    }
  }

  const renderFormContents = () => {
    if (isLoadingOwners) {
      return (
        <div className='flex justify-center items-center py-8'>
          <div className='text-gray-500'>Loading existing owners...</div>
        </div>
      )
    } else if (ownersError) {
      return (
        <div className='flex justify-center items-center py-8'>
          <div className='text-red-500'>Error loading existing owners. Please try again.</div>
        </div>
      )
    } else {
      return (
        <div className='flex flex-col gap-6'>
          {ownerFields.map((owner, index) => {
            const ownerData = watchedOwners[index]
            const ownerId = ownerData?.id
            const isControllingOwner = ownerData?.is_controlling_officer || false
            const blankOwner = owner.email === ''
            return (
              <OwnerEntry
                key={owner.id}
                index={index}
                defaultOpen={defaultCardsToOpen || blankOwner}
                onCreate={(ownerData) => handleOwnerCreate(ownerData, index)}
                onUpdate={handleOwnerUpdate}
                onDelete={(ownerId) => handleOwnerDelete(ownerId, index)}
                ownerId={ownerId}
                isControllingOwner={isControllingOwner}
              />
            )
          })}
          <div>
            {ownerFields.length >= 4
              ? (
                <div className='text-center py-4'>
                  <p className='text-sm text-gray-500'>
                    Maximum of 4 owners reached. Delete an owner to add a new one.
                  </p>
                </div>
                )
              : (
                <TextButton
                  leftIcon={Users2}
                  color='purple'
                  onClick={() => {
                    appendOwner({
                      first_name: '',
                      last_name: '',
                      title: '',
                      email: '',
                      is_beneficial_owner: true,
                      is_controlling_officer: false,
                      id: ''
                    })
                  }}
                >
                  Add {ownerFields.length ? 'another' : ''} owner
                </TextButton>
                )}
          </div>
        </div>
      )
    }
  }

  const controllingOwnerIsBeneficialOwner = watchedOwners.some(owner => owner.is_controlling_officer && owner.is_beneficial_owner)

  return (
    <FormProvider {...methods}>
      <CenteredLayout>
        <CenteredLayout.Header
          heading='Add owners'
          subheading={`Please identify any ${controllingOwnerIsBeneficialOwner ? 'additional ' : ''}individuals who own 25% or more of ${employer?.name || 'your company'}`}
          align='center'
        />
        <CenteredLayout.Container>
          {renderFormContents()}
        </CenteredLayout.Container>
        <CenteredLayout.Buttons align='center'>
          <Button
            variant='secondary'
            onClick={handleBack}
            type='button'
            disabled={isLoadingOwners || !!ownersError}
          >
            Back
          </Button>
          <Button
            variant='primary'
            onClick={methods.handleSubmit(handleSubmit)}
            rightIcon={ArrowRightIcon}
            disabled={methods.formState.isSubmitting || isLoadingOwners || !!ownersError}
          >
            {ownerFields.length === 0 ? 'Continue without owners' : 'Continue'}
          </Button>
        </CenteredLayout.Buttons>
        <TrustFooter />
      </CenteredLayout>
    </FormProvider>
  )
}
